import { palette, spaces } from '@kimuno/theme';
import {
	Alert,
	AlertTitle,
	Box,
	IconButton,
	type SxProps,
	type Theme,
	Typography,
} from '@mui/material';
import type { ReactNode } from 'react';

import CloseIcon from '../../assets/close.svg';
import useScreenSize from '../../utils/useScreenSize';

interface CustomMarketingAlertProps {
	isShow: boolean;
	startIcon: ReactNode;
	title: string;
	description?: string;
	actionButton: ReactNode;
	onClose: () => void;
	customAlertSx?: SxProps<Theme>;
}

const CustomMarketingAlert = ({
	isShow,
	startIcon,
	title,
	description,
	actionButton,
	onClose,
	customAlertSx,
}: CustomMarketingAlertProps) => {
	const { isXsScreen, isSmScreen } = useScreenSize();
	const isXsOrSmScreen = isXsScreen || isSmScreen;

	const actions = (
		<Box sx={{ display: 'flex', alignItems: 'center' }}>
			{!isXsOrSmScreen && actionButton}
			<IconButton
				data-testid="close-custom-marketing-alert"
				color="inherit"
				onClick={onClose}
				sx={{ padding: isXsOrSmScreen ? 0 : undefined }}
			>
				<CloseIcon />
			</IconButton>
		</Box>
	);

	if (!isShow) return null;

	return (
		<Alert
			severity="info"
			variant="filled"
			action={actions}
			icon={false}
			sx={{
				paddingX: spaces.md,
				paddingY: spaces.xs,
				borderRadius: 0,
				// The theme's MuiAlert override draws a 1px currentColor border — on the
				// filled banner that shows up as a white outline.
				border: 'none',
				backgroundColor: palette.primary.main,
				// The action slot's default top padding + flex-start alignment sink the
				// buttons below the text's centerline.
				'& .MuiAlert-action': {
					paddingTop: 0,
					alignItems: 'center',
				},
				...customAlertSx,
			}}
		>
			<Box sx={{ display: 'flex', alignItems: description ? 'flex-start' : 'center' }}>
				{startIcon}
				<Box>
					<AlertTitle sx={description ? undefined : { margin: 0 }}>{title}</AlertTitle>
					{description && <Typography variant="body2">{description}</Typography>}
				</Box>
			</Box>
			{isXsOrSmScreen && actionButton}
		</Alert>
	);
};

export default CustomMarketingAlert;
